package com.schoolconnect.app.feature.eventsnews

import com.schoolconnect.app.datasource.network.response.EventResponse
import com.schoolconnect.app.feature.common.PaginationPolicy

data class EventAttachmentDetailUi(
    val fileId: String,
    val name: String,
    val mimeType: String,
    val sizeLabel: String,
    val url: String = "",
    val previewBytes: ByteArray? = null
)

data class EventsNewsFilters(
    val eventType: String = "",
    val status: String = "",
    val publishedDate: String = "All",
    val publishedDateFrom: String = "",
    val publishedDateTo: String = ""
)

data class EventDraft(
    val title: String,
    val date: String,
    val description: String,
    val audience: String,
    val isActive: Boolean,
    val attachmentFileIds: List<String> = emptyList(),
    val eventId: String? = null,
    val removedAttachmentFileIds: List<String> = emptyList(),
    val classTargetIds: List<String> = emptyList(),
    val existingStatus: String? = null
)

data class EventAudienceOption(
    val code: String,
    val label: String
)

data class EventClassTargetOption(val id: String, val label: String)

data class EventRowUi(
    val id: String,
    val backendId: String = id,
    val title: String,
    val imageUrl: String = "",
    val eventType: String,
    val publishedOn: String,
    val expiryDate: String,
    val status: String,
    val description: String = "",
    val audience: String = "All Parents",
    val attachmentFileIds: List<String> = emptyList(),
    val attachmentFileUrls: List<String> = emptyList(),
    val attachments: List<EventAttachmentDetailUi> = emptyList()
)

data class EventsNewsUiState(
    val schoolId: String = "",
    val isLoading: Boolean = false,
    val isStatisticsLoading: Boolean = true,
    val errorMessage: String = "",
    val saveErrorMessage: String = "",
    val searchQuery: String = "",
    val filters: EventsNewsFilters = EventsNewsFilters(),
    val rows: List<EventRowUi> = emptyList(),
    val pageIndex: Int = 0,
    val pageSize: Int = PaginationPolicy.DEFAULT_PAGE_SIZE,
    val totalCount: Int = 0,
    val totalEvents: Int = 0,
    val draftEvents: Int = 0,
    val publishedEvents: Int = 0,
    val expiredEvents: Int = 0
    ,val sortField: EventsSortField = EventsSortField.PUBLISHED_DATE
    ,val sortAscending: Boolean = false
    ,val isSaving: Boolean = false
    ,val saveGeneration: Int = 0
    ,val viewingEvent: EventResponse? = null
    ,val eventAttachments: List<EventAttachmentDetailUi> = emptyList()
    ,val audienceOptions: List<EventAudienceOption> = emptyList()
    ,val classTargetOptions: List<EventClassTargetOption> = emptyList()
    ,val isViewingEvent: Boolean = false
    ,val viewEventError: String = ""
)

sealed interface EventsNewsUiEvent {
    data object RefreshClicked : EventsNewsUiEvent
    data class CreateSubmitted(val draft: EventDraft) : EventsNewsUiEvent
    data class UpdateSubmitted(val draft: EventDraft) : EventsNewsUiEvent
    data class DeleteEvent(val eventId: String) : EventsNewsUiEvent
    data class ViewEvent(val eventId: String) : EventsNewsUiEvent
    data class SearchChanged(val query: String) : EventsNewsUiEvent
    data class FiltersChanged(val filters: EventsNewsFilters) : EventsNewsUiEvent
    data object FiltersReset : EventsNewsUiEvent
    data class PageChanged(val pageIndex: Int) : EventsNewsUiEvent
    data class PageSizeChanged(val pageSize: Int) : EventsNewsUiEvent
    data class SortChanged(val field: EventsSortField) : EventsNewsUiEvent
}

enum class EventsSortField { ID, TITLE, AUDIENCE, PUBLISHED_DATE, EXPIRY_DATE, STATUS }

internal fun eventsComparator(field: EventsSortField): Comparator<EventRowUi> = when (field) {
    EventsSortField.ID -> compareBy { it.id }
    EventsSortField.TITLE -> compareBy { it.title }
    EventsSortField.AUDIENCE -> compareBy { it.audience }
    EventsSortField.PUBLISHED_DATE -> compareBy { it.publishedOn }
    EventsSortField.EXPIRY_DATE -> compareBy { it.expiryDate }
    EventsSortField.STATUS -> compareBy { it.status }
}
